import React, { useState } from 'react';
import { CarerRegisterRequest } from '../../types/index';
import { authService } from '../../services/authService';
import { useAuth } from '../../contexts/AuthContext';
import { Smartphone, User, Shield, Send, CheckCircle, ArrowRight } from 'lucide-react';
import SmsVerificationInput from './SmsVerificationInput';
import { useToastContext, handleApiError } from '../../contexts/ToastContext';

interface CarerRegisterPageProps {
  onRegisterSuccess?: (token: string, carer: any) => void;
  onSwitchToLogin?: () => void;
}

const CarerRegisterPage: React.FC<CarerRegisterPageProps> = ({ 
  onRegisterSuccess, 
  onSwitchToLogin 
}) => {
  const [step, setStep] = useState<'phone' | 'verify' | 'details'>('phone');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    role: 'self' as const
  });
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [userCheckLoading, setUserCheckLoading] = useState(false);
  
  const { register } = useAuth();
  const toast = useToastContext();

  // 验证手机号格式
  const isValidPhoneNumber = (phone: string) => {
    return /^1[3-9]\d{9}$/.test(phone);
  };

  // 第一步：发送验证码（含用户存在性检查）
  const handleSendSms = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!isValidPhoneNumber(phoneNumber)) {
      setError('请输入正确的手机号码');
      toast.error('格式错误', '请输入正确的手机号码');
      return;
    }

    setUserCheckLoading(true);
    setError(null);

    try {
      // 先检查用户是否已存在
      const userCheck = await authService.checkUserExists(phoneNumber);
      
      if (userCheck.exists) {
        setError('该手机号已注册照护者账户');
        toast.error('用户已存在', '该手机号已注册，请直接登录', {
          action: {
            label: '立即登录',
            onClick: () => {
              if (onSwitchToLogin) {
                onSwitchToLogin();
              }
            }
          }
        });
        return;
      }

      // 用户不存在，可以注册，发送验证码
      setLoading(true);
      await authService.sendSmsCode(phoneNumber, 'register');
      setStep('verify');
      toast.success('验证码已发送', `验证码已发送至 ${phoneNumber}，请查收`);
      console.log('✅ 注册验证码发送成功');
      
    } catch (err: any) {
      console.error('❌ 发送注册验证码失败:', err);
      
      const errorMessage = err.message || '发送验证码失败，请重试';
      setError(errorMessage);
      handleApiError(err, toast);
    } finally {
      setLoading(false);
      setUserCheckLoading(false);
    }
  };

  // 第二步：验证短信验证码
  const handleVerifySms = async (codeFromInput?: string) => {
    // 使用传入的验证码，如果没有则使用状态中的验证码
    const currentCode = codeFromInput || verificationCode;
    if (!currentCode || currentCode.length !== 6) {
      setError('请输入6位验证码');
      toast.error('验证码格式不完整', '请输入6位验证码');
      return;
    }

    // 🔧 修复：不在此处验证验证码，避免重复验证
    // 原问题：这里验证成功后会标记 verified=1，导致后续注册时找不到验证码
    // 修复方案：只做前端格式验证，实际验证在注册时进行
    setStep('details');
    toast.success('验证码已输入', '请完善个人信息后完成注册');
    console.log('✅ 验证码格式验证通过，等待注册时验证');
  };

  // 第三步：完成注册
  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      setError('请输入您的姓名');
      toast.error('信息不完整', '请输入您的姓名');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // 使用AuthContext的register方法，这样会自动更新React状态
      await register(phoneNumber, verificationCode, formData.name.trim(), formData.role);
      
      toast.success('注册成功', `欢迎加入ICOPE关怀助手，${formData.name}！`);
      console.log('✅ 照护者注册成功');
      
      if (onRegisterSuccess) {
        onRegisterSuccess('token', 'carer'); // 实际值由AuthContext管理
      }
    } catch (err: any) {
      console.error('❌ 注册失败:', err);
      const errorMessage = err.message || '注册失败，请重试';
      setError(errorMessage);
      handleApiError(err, toast);
    } finally {
      setLoading(false);
    }
  };

  // 重新发送验证码
  const handleResendSms = async () => {
    setLoading(true);
    setError(null);
    
    try {
      await authService.sendSmsCode(phoneNumber, 'register');
      console.log('✅ 验证码重新发送成功');
    } catch (err: any) {
      console.error('❌ 重新发送验证码失败:', err);
      setError(err.message || '重新发送失败，请稍后重试');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-green-50 flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        {/* 顶部Logo和标题 */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-4">
            <Shield className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            老年人整合照护助手
          </h1>
          <p className="text-gray-600">
            用户注册 - 让照护更轻松
          </p>
        </div>

        {/* 注册卡片 */}
        <div className="bg-white rounded-xl shadow-lg border p-6">
          {/* 步骤指示器 */}
          <div className="flex items-center justify-center mb-6">
            <div className="flex items-center space-x-4">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold
                ${step === 'phone' ? 'bg-blue-600 text-white' : 
                  ['verify', 'details'].includes(step) ? 'bg-green-500 text-white' : 'bg-gray-200 text-gray-500'}`}>
                1
              </div>
              <div className={`h-0.5 w-8 
                ${['verify', 'details'].includes(step) ? 'bg-green-500' : 'bg-gray-200'}`} />
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold
                ${step === 'verify' ? 'bg-blue-600 text-white' : 
                  step === 'details' ? 'bg-green-500 text-white' : 'bg-gray-200 text-gray-500'}`}>
                2
              </div>
              <div className={`h-0.5 w-8 
                ${step === 'details' ? 'bg-green-500' : 'bg-gray-200'}`} />
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold
                ${step === 'details' ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-500'}`}>
                3
              </div>
            </div>
          </div>

          {error && (
            <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-3">
              <p className="text-red-800 text-sm">{error}</p>
            </div>
          )}

          {/* 第一步：输入手机号 */}
          {step === 'phone' && (
            <form onSubmit={handleSendSms} className="space-y-4">
              <div className="text-center mb-4">
                <Smartphone className="w-12 h-12 text-blue-600 mx-auto mb-2" />
                <h3 className="text-lg font-semibold text-gray-900">输入手机号</h3>
                <p className="text-gray-600 text-sm">我们将发送验证码到您的手机</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  手机号码 *
                </label>
                <input
                  type="tel"
                  value={phoneNumber}
                  onChange={(e) => setPhoneNumber(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-lg"
                  placeholder="138****1234"
                  maxLength={11}
                  required
                />
              </div>

              <button
                type="submit"
                disabled={loading || userCheckLoading || !isValidPhoneNumber(phoneNumber)}
                className="w-full flex items-center justify-center space-x-2 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200 font-medium"
              >
                <Send className="w-5 h-5" />
                <span>
                  {userCheckLoading ? '检查用户中...' : 
                   loading ? '发送中...' : 
                   '发送验证码'}
                </span>
              </button>

              {/* 切换到登录 */}
              <div className="text-center pt-4 border-t">
                <p className="text-gray-600 text-sm">
                  已有账户？
                  <button
                    type="button"
                    onClick={onSwitchToLogin}
                    className="text-blue-600 hover:text-blue-700 font-medium ml-1"
                  >
                    立即登录
                  </button>
                </p>
              </div>
            </form>
          )}

          {/* 第二步：验证短信验证码 */}
          {step === 'verify' && (
            <div className="space-y-4">
              <div className="text-center mb-4">
                <CheckCircle className="w-12 h-12 text-green-600 mx-auto mb-2" />
                <h3 className="text-lg font-semibold text-gray-900">输入验证码</h3>
                <p className="text-gray-600 text-sm">
                  验证码已发送至 <span className="font-medium">{phoneNumber}</span>
                </p>
              </div>

              <SmsVerificationInput
                value={verificationCode}
                onChange={setVerificationCode}
                onComplete={handleVerifySms}
                loading={loading}
                onResend={handleResendSms}
              />

              <button
                type="button"
                onClick={() => setStep('phone')}
                className="w-full py-2 text-gray-600 hover:text-gray-800 transition-colors duration-200"
              >
                返回修改手机号
              </button>
            </div>
          )}

          {/* 第三步：完善信息 */}
          {step === 'details' && (
            <form onSubmit={handleRegister} className="space-y-4">
              <div className="text-center mb-4">
                <User className="w-12 h-12 text-blue-600 mx-auto mb-2" />
                <h3 className="text-lg font-semibold text-gray-900">完善信息</h3>
                <p className="text-gray-600 text-sm">最后一步，请填写您的基本信息</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  您的姓名 *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="请输入您的真实姓名"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  照护关系 *
                </label>
                <select
                  value={formData.role}
                  onChange={(e) => setFormData(prev => ({ ...prev, role: e.target.value as any }))}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                >
                  <option value="self">本人（我为自己创建档案）</option>
                  <option value="family">家庭照护者（为家人创建档案）</option>
                  <option value="professional">专业照护者（医护人员等）</option>
                  <option value="volunteer">志愿照护者</option>
                </select>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <p className="text-blue-800 text-sm">
                  📱 手机号: {phoneNumber} ✅ 已验证
                </p>
              </div>

              <button
                type="submit"
                disabled={loading || !formData.name.trim()}
                className="w-full flex items-center justify-center space-x-2 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200 font-medium"
              >
                <ArrowRight className="w-5 h-5" />
                <span>{loading ? '注册中...' : '完成注册'}</span>
              </button>

              <button
                type="button"
                onClick={() => setStep('verify')}
                className="w-full py-2 text-gray-600 hover:text-gray-800 transition-colors duration-200"
              >
                返回验证码输入
              </button>
            </form>
          )}
        </div>

        {/* 底部提示 */}
        <div className="text-center mt-6">
          <p className="text-gray-500 text-xs">
            注册即表示您同意我们的服务条款和隐私政策
          </p>
        </div>
      </div>
    </div>
  );
};

export default CarerRegisterPage;